import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface DailyPledge {
  id: string;
  user_id: string;
  pledge_date: string;
  morning_pledge_text: string | null;
  morning_commitment_level: number | null;
  morning_completed_at: string | null;
  evening_mood: string | null;
  evening_craving_level: number | null;
  evening_notes: string | null;
  evening_completed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export function useDailyPledge() {
  const { user } = useAuth();
  const [todayPledge, setTodayPledge] = useState<DailyPledge | null>(null);
  const [history, setHistory] = useState<DailyPledge[]>([]);
  const [loading, setLoading] = useState(true);

  const today = new Date().toISOString().split("T")[0];

  const fetchToday = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from("daily_pledges")
      .select("*")
      .eq("user_id", user.id)
      .eq("pledge_date", today)
      .maybeSingle();
    setTodayPledge(data as DailyPledge | null);
    setLoading(false);
  }, [user, today]);

  const fetchHistory = useCallback(async (limit = 30) => {
    if (!user) return;
    const { data } = await supabase
      .from("daily_pledges")
      .select("*")
      .eq("user_id", user.id)
      .order("pledge_date", { ascending: false })
      .limit(limit);
    setHistory((data as DailyPledge[]) ?? []);
  }, [user]);

  useEffect(() => {
    fetchToday();
    fetchHistory();
  }, [fetchToday, fetchHistory]);

  const submitMorningPledge = async (text: string, commitment: number) => {
    if (!user) return;
    const { error } = await supabase.from("daily_pledges").upsert(
      {
        user_id: user.id,
        pledge_date: today,
        morning_pledge_text: text,
        morning_commitment_level: commitment,
        morning_completed_at: new Date().toISOString(),
      },
      { onConflict: "user_id,pledge_date" }
    );
    if (!error) {
      await fetchToday();
      await fetchHistory();
    }
    return error;
  };

  const submitEveningCheckin = async (
    mood: string,
    cravingLevel: number,
    notes: string
  ) => {
    if (!user) return;
    const { error } = await supabase.from("daily_pledges").upsert(
      {
        user_id: user.id,
        pledge_date: today,
        evening_mood: mood,
        evening_craving_level: cravingLevel,
        evening_notes: notes,
        evening_completed_at: new Date().toISOString(),
      },
      { onConflict: "user_id,pledge_date" }
    );
    if (!error) {
      await fetchToday();
      await fetchHistory();
    }
    return error;
  };

  return {
    todayPledge,
    history,
    loading,
    submitMorningPledge,
    submitEveningCheckin,
    hasMorningPledge: !!todayPledge?.morning_pledge_text,
    hasEveningCheckin: !!todayPledge?.evening_mood,
  };
}
